import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';
import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * Naming a new list of favourites, wherever a shopper is asked to:
 *
 *   <FavouritesCreateForm
 *     variant={FavouritesCreateFormVariant.STACKED}
 *     noValidate
 *     onSubmit={handleSubmit(onSubmit)}
 *   >
 *     <FavouritesCreateFormField>
 *       <Input … />
 *     </FavouritesCreateFormField>
 *     <DialogFooter>…cancel and submit…</DialogFooter>
 *   </FavouritesCreateForm>
 *
 * **One form, asked for in two places.** A dialog opened from the favourites
 * page asks for the name on its own; the modal a product is being saved from
 * asks for it beside everything else, so the field and the submit stand on one
 * line. That is one form laying itself out two ways — an appearance arm — and
 * everything around it is composition: the dialog's chrome comes from
 * `@vs-shop/ui/dialog`, the inline panel from the parts below.
 *
 * **Nothing about a form crosses into this package.** No schema, no resolver,
 * no atom, no toast: `onSubmit` and the field arrive from tenant JSX, which is
 * also where validation and the request live.
 *
 * Server-first: every part here is a plain component, and the interactive
 * pieces are the primitives a tenant composes into them.
 */

/**
 * How the form arranges what it holds.
 *
 * The stacked arm asks for nothing — a form is already a block, and stating a
 * `flex flex-col` it does not need would be a class with no work to do. The
 * part drops the attribute entirely when the arm resolves empty rather than
 * stamping `class=""` on every dialog.
 *
 * No `unstyled` arm, for the same reason: `stacked` already emits nothing, so a
 * third name emitting nothing would be a synonym rather than an opt-out.
 */
export const favouritesCreateFormVariants = cva('', {
  variants: {
    variant: {
      /** Field above footer, for a form that has a surface to itself. */
      stacked: '',
      /** Field beside submit, for a form standing inside something else. */
      inline: 'flex gap-2',
    },
  },
});

type FavouritesCreateFormVariantName = NonNullable<
  VariantProps<typeof favouritesCreateFormVariants>['variant']
>;

export type FavouritesCreateFormVariant = FavouritesCreateFormVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const FavouritesCreateFormVariant = {
  /** Field above footer. */
  STACKED: 'stacked',
  /** Field beside submit. */
  INLINE: 'inline',
} as const satisfies Record<string, FavouritesCreateFormVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type FavouritesCreateFormVariantProps = { readonly variant: FavouritesCreateFormVariant };

export type FavouritesCreateFormProps = ComponentProps<'form'> & FavouritesCreateFormVariantProps;

/**
 * The form a list is named in.
 *
 * Submission is the tenant's: this renders the element and takes `onSubmit`,
 * `noValidate` and the rest natively.
 */
export const FavouritesCreateForm = ({
  className,
  variant,
  ...props
}: FavouritesCreateFormProps) => (
  <form
    data-slot="favourites-create-form"
    className={optionalClassName(clsxm(favouritesCreateFormVariants({ variant }), className))}
    {...props}
  />
);

/** The room the field is given inside a form that stacks. */
const favouritesCreateFormFieldClasses = 'py-4';

export type FavouritesCreateFormFieldProps = ComponentProps<'div'>;

/** Where the name is typed. */
export const FavouritesCreateFormField = ({
  className,
  ...props
}: FavouritesCreateFormFieldProps) => (
  <div
    data-slot="favourites-create-form-field"
    className={clsxm(favouritesCreateFormFieldClasses, className)}
    {...props}
  />
);

/** The way to ask for the form when it is not showing: a mark and a word. */
const favouritesCreateDisclosureClasses = 'flex items-center gap-2';

export type FavouritesCreateDisclosureProps = ComponentProps<'button'>;

/**
 * The offer to name a new list.
 *
 * A plain `<button>`; whether it is showing the form is the tenant's state, and
 * what it is called is the tenant's copy. `type="button"` by default and
 * overridable — this stands inside a modal that has forms of its own.
 */
export const FavouritesCreateDisclosure = ({
  className,
  type,
  ...props
}: FavouritesCreateDisclosureProps) => (
  <button
    data-slot="favourites-create-disclosure"
    type={type ?? 'button'}
    className={clsxm(favouritesCreateDisclosureClasses, className)}
    {...props}
  />
);

/** The offer's word, set at the size the copy around it runs at. */
const favouritesCreateDisclosureLabelClasses = 'text-base/5';

export type FavouritesCreateDisclosureLabelProps = ComponentProps<'span'>;

/** What the offer says. */
export const FavouritesCreateDisclosureLabel = ({
  className,
  ...props
}: FavouritesCreateDisclosureLabelProps) => (
  <span
    data-slot="favourites-create-disclosure-label"
    className={clsxm(favouritesCreateDisclosureLabelClasses, className)}
    {...props}
  />
);

/**
 * The panel the form stands in once the offer has been taken up.
 *
 * Tinted in the brand's own colour rather than the surface's: it appears in
 * place, inside a modal already full of controls, and has to read as something
 * that just opened.
 */
const favouritesCreatePanelClasses =
  'border-primary/25 bg-primary/10 flex flex-col gap-2 rounded-lg border p-3';

export type FavouritesCreatePanelProps = ComponentProps<'div'>;

/** The inline form's panel. */
export const FavouritesCreatePanel = ({ className, ...props }: FavouritesCreatePanelProps) => (
  <div
    data-slot="favourites-create-panel"
    className={clsxm(favouritesCreatePanelClasses, className)}
    {...props}
  />
);

/** The panel's top line: what it is, and the way out of it. */
const favouritesCreatePanelHeaderClasses = 'flex items-center justify-between';

export type FavouritesCreatePanelHeaderProps = ComponentProps<'div'>;

/** The panel's header. */
export const FavouritesCreatePanelHeader = ({
  className,
  ...props
}: FavouritesCreatePanelHeaderProps) => (
  <div
    data-slot="favourites-create-panel-header"
    className={clsxm(favouritesCreatePanelHeaderClasses, className)}
    {...props}
  />
);

/** What the panel is for. A neutral element: the page's outline is the page's. */
const favouritesCreatePanelTitleClasses = 'text-base font-medium';

export type FavouritesCreatePanelTitleProps = ComponentProps<'div'> & {
  /** The element to paint instead of the neutral `<div>`. */
  readonly render?: RenderElement;
};

/** The panel's name for itself. */
export const FavouritesCreatePanelTitle = ({
  className,
  render,
  ...props
}: FavouritesCreatePanelTitleProps) => (
  <RenderedPart
    dataSlot="favourites-create-panel-title"
    className={clsxm(favouritesCreatePanelTitleClasses, className)}
    render={render}
    {...props}
  />
);

/** The way to put the panel away again, quiet until a pointer finds it. */
const favouritesCreatePanelCloseClasses = 'text-foreground-muted hover:text-foreground p-1';

export type FavouritesCreatePanelCloseProps = ComponentProps<'button'>;

/**
 * The panel's dismiss.
 *
 * Carries no label of its own — what it is called is copy, and copy is the
 * tenant's, so `aria-label` rides the native prop spread.
 */
export const FavouritesCreatePanelClose = ({
  className,
  type,
  ...props
}: FavouritesCreatePanelCloseProps) => (
  <button
    data-slot="favourites-create-panel-close"
    type={type ?? 'button'}
    className={clsxm(favouritesCreatePanelCloseClasses, className)}
    {...props}
  />
);
